// ─── Backend ────────────────────────────────────────────────────────────────
// Same Supabase project as the native app, hit from the browser. RLS is the
// boundary. Reads: daily_challenges + get_leaderboard RPC. Writes: one anon
// score per day. Everything degrades to an offline-safe path.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './config';
import { todayDateString, mulberry32 } from '@game/seed';
import { pickTheme } from '@game/themeNames';
import type { LeaderboardEntry } from '@gametypes';
import { WEB_SKINS, BUNDLE_ENTITLEMENT_ID, BUNDLE_SKIN_IDS } from './skins';

const online = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
const supabase: SupabaseClient | null = online
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      // supabase-js defaults to localStorage on web; the anon session persists
      // so a returning visitor keeps the same user_id (no PII, no fingerprint).
      auth: { persistSession: true, autoRefreshToken: true },
    })
  : null;

export interface DailyBoard {
  date: string;
  seed: string;
  themeName: string;
  fromNetwork: boolean;
}

/**
 * The anonymous Supabase session's user id. game_scores RLS is
 * `WITH CHECK (auth.uid() = user_id)`, so writes MUST use this id, not a
 * client-invented one. Mirrors src/api/useAnonymousAuth.ts (native).
 * Requires "Allow anonymous sign-ins" enabled in the Supabase project.
 */
let _uidPromise: Promise<string | null> | null = null;
export function getUserId(): Promise<string | null> {
  if (!supabase) return Promise.resolve(null);
  if (!_uidPromise) {
    _uidPromise = (async () => {
      try {
        const { data: s } = await supabase.auth.getSession();
        if (s.session?.user?.id) return s.session.user.id;
        const { data, error } = await supabase.auth.signInAnonymously();
        if (error || !data.user?.id) return null;
        return data.user.id;
      } catch {
        return null;
      }
    })();
  }
  return _uidPromise;
}

/** Today's board. Network row if available, else the deterministic local fallback. */
export async function fetchDailyBoard(): Promise<DailyBoard> {
  const date = todayDateString();
  const local: DailyBoard = {
    date,
    seed: date, // migration 001: daily_challenges.seed == date string
    themeName: pickTheme(date),
    fromNetwork: false,
  };
  if (!supabase) return local;
  try {
    const { data, error } = await supabase
      .from('daily_challenges')
      .select('date, seed, theme_name')
      .eq('date', date)
      .maybeSingle();
    if (error || !data) return local;
    return { date: data.date, seed: data.seed, themeName: data.theme_name, fromNetwork: true };
  } catch {
    return local;
  }
}

export async function fetchLeaderboard(date: string): Promise<LeaderboardEntry[]> {
  if (!supabase) return [];
  try {
    const { data, error } = await supabase.rpc('get_leaderboard', { date_str: date });
    if (error || !data) return [];
    return (data as Array<Record<string, number | string>>).map((row) => ({
      rank: Number(row.rank),
      score: Number(row.score),
      chainsCleared: Number(row.chains_cleared),
      maxCombo: Number(row.max_combo),
      onBeatCount: Number(row.on_beat_count),
      submittedAt: String(row.submitted_at),
    }));
  } catch {
    return [];
  }
}

export interface SubmitPayload {
  challengeDate: string;
  score: number;
  chainsCleared: number;
  onBeatCount: number;
  maxCombo: number;
  totalMoves: number;
  moves: unknown[];
}

const QUEUE_KEY = 'sb_score_queue';

async function upsertScore(uid: string, p: SubmitPayload): Promise<boolean> {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from('game_scores').upsert(
      {
        user_id: uid,
        challenge_date: p.challengeDate,
        score: p.score,
        chains_cleared: p.chainsCleared,
        on_beat_count: p.onBeatCount,
        max_combo: p.maxCombo,
        total_moves: p.totalMoves,
        moves: p.moves,
      },
      { onConflict: 'user_id,challenge_date' },
    );
    return !error;
  } catch {
    return false;
  }
}

/** Submit today's score. Queues to localStorage on failure; drained on next load. */
export async function submitScore(p: SubmitPayload): Promise<void> {
  const uid = await getUserId();
  if (!uid || !(await upsertScore(uid, p))) enqueue(p);
}

function enqueue(p: SubmitPayload): void {
  try {
    const q = JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as SubmitPayload[];
    q.push(p);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-20)));
  } catch {
    /* ignore */
  }
}

export async function drainScoreQueue(): Promise<void> {
  if (!supabase) return;
  let q: SubmitPayload[] = [];
  try {
    q = JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]');
  } catch {
    return;
  }
  if (q.length === 0) return;
  const uid = await getUserId();
  if (!uid) return;
  const remaining: SubmitPayload[] = [];
  for (const p of q) {
    if (!(await upsertScore(uid, p))) remaining.push(p);
  }
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
  } catch {
    /* ignore */
  }
}

/** Local personal best for today, keyed by date. */
export function getPersonalBest(date: string): number {
  try {
    const raw = JSON.parse(localStorage.getItem('sb_pb') ?? '{}') as Record<string, number>;
    return raw[date] ?? 0;
  } catch {
    return 0;
  }
}

export function savePersonalBest(date: string, score: number): void {
  try {
    const raw = JSON.parse(localStorage.getItem('sb_pb') ?? '{}') as Record<string, number>;
    raw[date] = Math.max(raw[date] ?? 0, score);
    localStorage.setItem('sb_pb', JSON.stringify(raw));
  } catch {
    /* ignore */
  }
}

// ─── Cosmetic IAP (Stripe) ──────────────────────────────────────────────────
// docs/spec.md §4 "2026-09-17 — Web IAP override" / tasks.json 8.9. Mirrors
// the applyCustomerInfo() expansion logic in src/api/useIAP.ts: owning the
// bundle entitlement unlocks every bundle-eligible skin, regardless of
// whether the individual entitlement rows exist. Skin ids/entitlement ids
// are sourced from ./skins (single catalogue, not duplicated here).

/** Unlocked skin ids for the current anon session, expanding the bundle entitlement. */
export async function fetchUnlockedSkins(): Promise<string[]> {
  if (!supabase) return [];
  const uid = await getUserId();
  if (!uid) return [];
  try {
    const { data, error } = await supabase
      .from('web_entitlements')
      .select('entitlement_id')
      .eq('user_id', uid);
    if (error || !data) return [];
    const owned = new Set(data.map((r) => r.entitlement_id as string));
    if (owned.has(BUNDLE_ENTITLEMENT_ID)) return BUNDLE_SKIN_IDS;
    return WEB_SKINS.filter((s) => s.entitlementId && owned.has(s.entitlementId)).map((s) => s.id);
  } catch {
    return [];
  }
}

/**
 * Starts a Stripe Checkout for one entitlement and redirects the browser on
 * success. Returns an error message on failure, or null once the redirect
 * has started (there is no "success" return — the page navigates away).
 */
export async function startCheckout(entitlementId: string): Promise<string | null> {
  if (!supabase || !SUPABASE_URL) return 'Store not available yet';
  const uid = await getUserId();
  if (!uid) return 'Store not available yet';
  const { data: s } = await supabase.auth.getSession();
  const token = s.session?.access_token;
  if (!token) return 'Store not available yet';
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ entitlementId }),
    });
    const data = (await res.json()) as { url?: string; error?: string };
    if (!res.ok || !data.url) return data.error ?? 'Store not available yet';
    window.location.href = data.url;
    return null;
  } catch {
    return 'Store not available yet';
  }
}

export { mulberry32 };
